import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BaseChartDirective } from 'ng2-charts';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

import { ComingSoonComponent } from '../shared/coming-soon/coming-soon';
import { AuthLayoutComponent } from '../shared/_layouts/auth-layout/auth-layout.component';
import { FullComponent } from '../shared/_layouts/full/full.component';

import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { WebConsoleRestrictedComponent } from './components/web-console-restricted/web-console-restricted.component';
import { AuditComponent } from './components/audit/audit.component';
import { BarChartComponent } from './components/bar-chart/bar-chart.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { CashFlowChartComponent } from './components/cash-flow-chart/cash-flow-chart.component';
import { ChangeHistoryDialogComponent } from './components/change-history-dialog/change-history-dialog.component';
import { ReasonDialogComponent } from './components/reason-dialog/reason-dialog.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { ImagePreviewDialogComponent } from './components/image-preview-dialog/image-preview-dialog.component';
import { LineChartComponent } from './components/line-chart/line-chart.component';
import { PhoneInputComponent } from './components/phone-input/phone-input.component';
import { PieChartComponent } from './components/pie-chart/pie-chart.component';
import { ScatterChartComponent } from './components/scatter-chart/scatter-chart.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { TurnstileComponent } from './components/turnstile/turnstile.component';
import { WeatherChartComponent } from './components/weather-chart/weather-chart.component';
import { AmountColorDirective } from './directives/amount-color/amount-color.directive';
import { AlphaNumericOnlyDirective } from './directives/alpah-numeric-input-controller/alpha-numeric.directive';
import { NumericOnlyDirective } from './directives/alpah-numeric-input-controller/numeric-only.directive';
import { CapitalizeEveryDirective } from './directives/capitalize-every/capitalize-every.directive';
import { CapitalizeFirstDirective } from './directives/capitalize-first/capitalize-first.directive';
import { FileInputResetDirective } from './directives/file-input-reset/file-input-reset.directive';
import { TrimSelectSearchDirective } from './directives/trim-select-search/trim-select-search.directive';
import { TwoStateSortDirective } from './directives/two-state-sort/two-state-sort.directive';
import { MaterialModule } from './material/material.module';
import { LeadershipTitleKeyPipe } from './pipes/leadership-title-key.pipe';
import { OverrideDisplayDescriptionPipe } from './pipes/override-display-description.pipe';
import { OverrideDisplayNamePipe } from './pipes/override-display-name.pipe';
import { StatusDisplayNamePipe } from './pipes/status-display-name.pipe';
import { RotarySpinnerComponent } from './components/rotary-spinner/rotary-spinner.component';
import { PasswordService } from './services/password.service';

@NgModule({
  declarations: [
    AmountColorDirective,
    AlphaNumericOnlyDirective,
    NumericOnlyDirective,
    CapitalizeFirstDirective,
    CapitalizeEveryDirective,
    FileInputResetDirective,
    FullComponent,
    AuthLayoutComponent,
    SideNavComponent,
    ConfirmDialogComponent,
    ChangeHistoryDialogComponent,
    ReasonDialogComponent,
    BreadcrumbComponent,
    AccessDeniedComponent,
    WebConsoleRestrictedComponent,
    ComingSoonComponent,
    TurnstileComponent,
    PieChartComponent,
    BarChartComponent,
    LineChartComponent,
    ScatterChartComponent,
    CashFlowChartComponent,
    WeatherChartComponent,
    AuditComponent,
    ImagePreviewDialogComponent,
    TrimSelectSearchDirective,
    TwoStateSortDirective,
    RotarySpinnerComponent,
    PhoneInputComponent
  ],
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatMenuModule,
    MatCardModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDialogModule,
    MaterialModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxMatSelectSearchModule,
    ZXingScannerModule,
    BaseChartDirective,
    MatGridListModule,
    StatusDisplayNamePipe,
    OverrideDisplayNamePipe,
    OverrideDisplayDescriptionPipe,
    LeadershipTitleKeyPipe
  ],
  exports: [
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatMenuModule,
    MatCardModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDialogModule,
    MaterialModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxMatSelectSearchModule,
    ZXingScannerModule,
    AmountColorDirective,
    AlphaNumericOnlyDirective,
    NumericOnlyDirective,
    CapitalizeFirstDirective,
    CapitalizeEveryDirective,
    FileInputResetDirective,
    BaseChartDirective,
    MatGridListModule,
    PieChartComponent,
    BarChartComponent,
    LineChartComponent,
    ScatterChartComponent,
    CashFlowChartComponent,
    WeatherChartComponent,
    TurnstileComponent,
    AuditComponent,
    StatusDisplayNamePipe,
    OverrideDisplayNamePipe,
    OverrideDisplayDescriptionPipe,
    LeadershipTitleKeyPipe,
    TrimSelectSearchDirective,
    TwoStateSortDirective,
    RotarySpinnerComponent,
    PhoneInputComponent
  ],
  providers: [StatusDisplayNamePipe, OverrideDisplayNamePipe, OverrideDisplayDescriptionPipe, PasswordService]
})
export class SharedModule {}
